/**
 * The rules behind the Client checks page. Pure functions: no reads, no writes.
 *
 * Two kinds of check:
 *   - record checks read only the client configs and the portal's own record
 *     of each client (contract, organisation, trial dates, tags);
 *   - usage checks also read the fleet index the nightly rollup builds: each
 *     client's daily use, and whether its last nightly read worked.
 *
 * A day is a `yyyy-mm-dd` string. The windows count back from `today`, the
 * date in New Zealand: the last 30 days are the 30 days that end on `today`.
 */
import { ACCOUNT_ORG_DISPLAY } from '@/types';
import type { AccountOrgValue, ClientConfig, ClientMetadata } from '@/types';
import { billingFor, resolveContract } from '@/config/contract';
import { contractText } from '@/utils/contractText';

type DayMap = Record<string, number> | undefined;

/** The parts of a client's snapshot the checks read. A full snapshot fits. */
export interface UseSnapshot {
  chat?: {
    daily_convs_started?: DayMap;
    daily_convs?: DayMap;
    daily_adhoc_active_users?: DayMap;
    daily_active_users?: DayMap;
  };
  scheduled_runs?: { daily_runs?: DayMap };
  credits?: {
    daily_credits?: DayMap;
    current_month?: string;
    current_month_used?: number;
    first_activity_ts?: string | null;
  };
  cost_explorer?: { totals_by_day?: DayMap; model_lines_daily?: DayMap };
  client_config?: { product?: string | null };
}

/** How the client's last nightly read went. */
export interface StackStatus {
  ok: boolean;
  lastOkAt?: string;
  lastError?: string;
}

export interface CheckClient {
  name: string;
  config: Pick<ClientConfig, 'contractType' | 'devInstance'>;
}

// ── days ─────────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The date in New Zealand, `yyyy-mm-dd`. */
export function nzToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** The `yyyy-mm-dd` at the start of a date or a timestamp, or null when it is not one. */
export function toDay(value: string | null | undefined): string | null {
  const day = (value ?? '').trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(Date.parse(`${day}T00:00:00Z`)) ? day : null;
}

const dayMs = (day: string) => Date.parse(`${day}T00:00:00Z`);

/** Whole days from `from` to `to`. Negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((dayMs(to) - dayMs(from)) / DAY_MS);
}

export function addDays(day: string, days: number): string {
  return new Date(dayMs(day) + days * DAY_MS).toISOString().slice(0, 10);
}

/** "14 Jul 2026". */
export function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** "Nov 2026" from `yyyy-mm`. */
export function formatMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

/** The sum of a daily map over the `days` days that end on `today`. */
export function sumWindow(map: DayMap, today: string, days: number): number {
  if (!map) return 0;
  const after = addDays(today, -days);
  let total = 0;
  for (const [day, value] of Object.entries(map)) {
    if (day > after && day <= today && Number.isFinite(value)) total += value;
  }
  return total;
}

/** The largest value of a daily map over the `days` days that end on `today`. Null when the map is absent. */
function maxWindow(map: DayMap, today: string, days: number): number | null {
  if (!map) return null;
  const after = addDays(today, -days);
  let most = 0;
  for (const [day, value] of Object.entries(map)) {
    if (day > after && day <= today && Number.isFinite(value)) most = Math.max(most, value);
  }
  return most;
}

const daysWithUse = (map: DayMap): string[] =>
  Object.entries(map ?? {})
    .filter(([day, value]) => value > 0 && toDay(day) === day)
    .map(([day]) => day);

// ── use ──────────────────────────────────────────────────────────────────

export interface StackUse {
  /** Conversations started in the last 30 and 60 days. */
  convs30: number;
  convs60: number;
  /** Scheduled runs in the last 60 days. */
  runs60: number;
  /** Credits used in the last 30 and 60 days. */
  credits30: number;
  credits60: number;
  /** Model spend on the AWS bill in the last 60 days, in US dollars. */
  modelSpend60: number;
  /** The most people who typed a message on one day in the last 30 days. Null when the data has no such count. */
  peoplePeak30: number | null;
  /** The last day with a conversation, a scheduled run or credits. Null when there is none on record. */
  lastUseDay: string | null;
  /** The first day the stack is known to exist. Null when the data cannot tell. */
  stackSince: string | null;
}

/**
 * What the client used, from its snapshot.
 *
 * `stackSince` is the earliest of: the first day with use, and the first day
 * its AWS account cost money, leaving out the first of each month. An unused
 * NextGen account waits in a pool before a client gets it, and AWS bills a
 * few monthly charges on the 1st even then. A deployed stack costs money
 * every day.
 */
export function stackUse(snapshot: UseSnapshot, today: string): StackUse {
  const chat = snapshot.chat ?? {};
  // Conversations by the day they started. An older snapshot lacks it: its count by active day still shows use.
  const convs = chat.daily_convs_started ?? chat.daily_convs;
  const runs = snapshot.scheduled_runs?.daily_runs;
  const credits = snapshot.credits?.daily_credits;
  const used = [...daysWithUse(convs), ...daysWithUse(runs), ...daysWithUse(credits)].sort();

  const costDays = Object.entries(snapshot.cost_explorer?.totals_by_day ?? {})
    .filter(([day, cost]) => cost >= 0.01 && toDay(day) === day && !day.endsWith('-01'))
    .map(([day]) => day);
  const firstActivity = toDay(snapshot.credits?.first_activity_ts);
  const since = [...costDays, ...used, ...(firstActivity ? [firstActivity] : [])].sort();

  return {
    convs30: sumWindow(convs, today, 30),
    convs60: sumWindow(convs, today, 60),
    runs60: sumWindow(runs, today, 60),
    credits30: sumWindow(credits, today, 30),
    credits60: sumWindow(credits, today, 60),
    modelSpend60: sumWindow(snapshot.cost_explorer?.model_lines_daily, today, 60),
    peoplePeak30: maxWindow(chat.daily_adhoc_active_users ?? chat.daily_active_users, today, 30),
    lastUseDay: used.length > 0 ? used[used.length - 1] : null,
    stackSince: since[0] ?? null,
  };
}

// ── trials ───────────────────────────────────────────────────────────────

export interface TrialTiming {
  state: 'ended' | 'no-end' | 'running';
  /** Days since the end for an ended trial, days left for a running one. Null with no end date. */
  days: number | null;
  text: string;
}

/** Where a trial stands on `today`. The end date is the trial's last day. */
export function trialTiming(endDate: string | undefined, today: string): TrialTiming {
  const end = toDay(endDate);
  if (!end) return { state: 'no-end', days: null, text: 'No end date' };
  const left = daysBetween(today, end);
  if (left < 0) {
    const ago = -left;
    return { state: 'ended', days: ago, text: ago === 1 ? 'Ended yesterday' : `Ended ${ago} days ago` };
  }
  if (left === 0) return { state: 'running', days: 0, text: 'Ends today' };
  return { state: 'running', days: left, text: left === 1 ? '1 day left' : `${left} days left` };
}

export interface TrialRow {
  clientName: string;
  startDate: string | null;
  endDate: string | null;
  timing: TrialTiming;
}

/**
 * Every active client on the trial contract. Ended trials come first, the
 * longest ended at the top, then trials with no end date, then running
 * trials, the soonest to end at the top.
 */
export function trialRows(
  clients: readonly CheckClient[],
  row: (clientName: string) => ClientMetadata | undefined,
  isInactive: (clientName: string) => boolean,
  today: string
): TrialRow[] {
  const rank = { ended: 0, 'no-end': 1, running: 2 } as const;
  return clients
    .filter((c) => !isInactive(c.name) && resolveContract(row(c.name), c.config) === 'trial')
    .map((c) => {
      const meta = row(c.name);
      return {
        clientName: c.name,
        startDate: toDay(meta?.trialStartDate),
        endDate: toDay(meta?.trialEndDate),
        timing: trialTiming(meta?.trialEndDate, today),
      };
    })
    .sort((a, b) => {
      if (a.timing.state !== b.timing.state) return rank[a.timing.state] - rank[b.timing.state];
      if (a.timing.state === 'ended') return (b.timing.days ?? 0) - (a.timing.days ?? 0);
      if (a.timing.state === 'running') return (a.timing.days ?? 0) - (b.timing.days ?? 0);
      return a.clientName.localeCompare(b.clientName);
    });
}

// ── checks ───────────────────────────────────────────────────────────────

export type CheckId = 'no-contract' | 'contract-vs-org' | 'inactive-but-used' | 'idle' | 'unreadable' | 'no-stack';

/** What a person can do about a problem from the page. */
export type CheckAction = 'change-config' | 'open-configs' | 'mark-inactive' | 'none';

export interface CheckDef {
  id: CheckId;
  title: string;
  /** What the check looks for, in one or two sentences. */
  help: string;
  actions: CheckAction[];
  /** The check needs the fleet data. */
  usage: boolean;
}

export const IDLE_DAYS = 60;
/**
 * Model spend below this in the idle window is not use: an unused stack still
 * pays a little for its knowledge base. A stack that runs agents and no chat,
 * such as a pipeline, shows its use here.
 */
export const MODEL_USE_FLOOR_USD = 5;
export const RECENT_DAYS = 30;

/** The checks, in the order the page shows them. */
export const CHECKS: readonly CheckDef[] = [
  {
    id: 'no-contract',
    title: 'No contract',
    help: 'Every client needs a contract. Set it in the client config.',
    actions: ['change-config'],
    usage: false,
  },
  {
    id: 'contract-vs-org',
    title: 'The contract disagrees with who pays AWS',
    help: "Own account means the client pays AWS. That fits only an AWS account the client owns, outside NextGen and Arcanum's organisation.",
    actions: ['change-config', 'open-configs'],
    usage: false,
  },
  {
    id: 'inactive-but-used',
    title: 'Inactive, but in use',
    help: `Marked inactive, and used in the last ${RECENT_DAYS} days. Lift the tag in Configs if the client is back.`,
    actions: ['open-configs'],
    usage: true,
  },
  {
    id: 'idle',
    title: `No use for ${IDLE_DAYS} days`,
    help: `Active customer stacks with no conversations, scheduled runs, credits or model spend in the last ${IDLE_DAYS} days. Nolia stacks, dev stacks, and stacks too new to tell, are left out.`,
    actions: ['mark-inactive'],
    usage: true,
  },
  {
    id: 'unreadable',
    title: "The stack's account cannot be read",
    help: "The nightly read can no longer reach these stacks' AWS accounts. Their figures elsewhere are from the last good read.",
    actions: ['mark-inactive'],
    usage: true,
  },
  {
    id: 'no-stack',
    title: 'No stack yet',
    help: 'A config the nightly read has no data for: a new client before its first deploy. Nothing to do until it is deployed.',
    actions: ['none'],
    usage: true,
  },
];

export const CHECK_BY_ID: Record<CheckId, CheckDef> = Object.fromEntries(CHECKS.map((c) => [c.id, c])) as Record<
  CheckId,
  CheckDef
>;

/** The reason saved on the inactive tag when a person marks a client inactive from a check. */
export const inactiveReason = (check: CheckId): string => `Client checks: ${CHECK_BY_ID[check].title}.`;

export interface Finding {
  check: CheckId;
  clientName: string;
  /** The problem, in plain words. */
  problem: string;
  /** A second line of fact: when, or what the nightly read said. */
  detail?: string;
  /** The error the nightly read gave, in full, for the hover text. */
  error?: string;
}

/**
 * A nightly read error that says the stack's AWS account cannot be reached:
 * what it means for a person, or null for any other error. A read that ran
 * out of time or memory is a fault of the read, not of the account.
 */
export function unreadableReason(error: string | undefined): string | null {
  if (!error) return null;
  if (/NoSuchBucket/.test(error)) return 'Its storage bucket no longer exists.';
  if (/AccessDenied/.test(error) && /AssumeRole/.test(error)) {
    return 'Arcanum can no longer sign in to its AWS account.';
  }
  if (/AccessDenied|InvalidClientTokenId|UnrecognizedClientException/.test(error)) {
    return 'Its AWS account refuses the nightly read.';
  }
  return null;
}

/** The first part of an error, to name it in a sentence. */
const shortError = (error: string) => (error.length > 120 ? `${error.slice(0, 117)}...` : error);

const plural = (n: number, one: string, many: string) =>
  `${Math.round(n).toLocaleString()} ${Math.round(n) === 1 ? one : many}`;

const ORG_PLACE: Record<AccountOrgValue, string> = {
  nextgen: 'the NextGen organisation',
  arcanum: "Arcanum's own organisation",
  standalone: "the client's own account",
};

export interface CheckInput {
  clients: readonly CheckClient[];
  row: (clientName: string) => ClientMetadata | undefined;
  isInactive: (clientName: string) => boolean;
  /** The fleet data. Null until it is in: the usage checks then find nothing. */
  fleet: {
    snapshot: (clientName: string) => UseSnapshot | undefined;
    status: (clientName: string) => StackStatus | undefined;
  } | null;
  today: string;
}

/** Every problem the checks find, one finding for each problem, in the order of `CHECKS`, then by client. */
export function runChecks({ clients, row, isInactive, fleet, today }: CheckInput): Finding[] {
  const found: Finding[] = [];
  const add = (finding: Finding) => found.push(finding);

  for (const client of clients) {
    const name = client.name;
    const meta = row(name);
    const inactive = isInactive(name);
    const contract = resolveContract(meta, client.config);

    // ── records ──
    if (!inactive && !contract) add({ check: 'no-contract', clientName: name, problem: 'No contract is set.' });

    const org = meta?.accountOrg;
    if (!inactive && contract && org) {
      const words = contractText(meta, client.config) ?? contract;
      const paysAwsItself = billingFor(contract, client.config.contractType) === 'own-account';
      if (paysAwsItself && org !== 'standalone') {
        add({
          check: 'contract-vs-org',
          clientName: name,
          problem: `The contract is ${words}, so the client pays AWS. Its AWS account is in ${ORG_PLACE[org]}, so Arcanum pays AWS.`,
        });
      } else if (!paysAwsItself && org === 'standalone') {
        add({
          check: 'contract-vs-org',
          clientName: name,
          problem: `Its AWS account is the client's own (${ACCOUNT_ORG_DISPLAY.standalone.label}), so the client pays AWS. The contract is ${words}.`,
        });
      }
    }

    // ── use ──
    if (!fleet) continue;
    const snapshot = fleet.snapshot(name);
    const status = fleet.status(name);
    if (!snapshot) {
      // A config before its first deploy has no data. It must never be offered for marking inactive.
      if (!inactive) {
        add({
          check: 'no-stack',
          clientName: name,
          problem: 'The nightly read has no data for this client.',
          ...(status?.lastError
            ? { detail: `The nightly read says: ${shortError(status.lastError)}`, error: status.lastError }
            : {}),
        });
      }
      continue;
    }
    const use = stackUse(snapshot, today);

    if (inactive && (use.convs30 > 0 || use.credits30 > 0)) {
      const parts = [
        use.convs30 > 0 ? plural(use.convs30, 'conversation', 'conversations') : '',
        use.credits30 > 0 ? plural(use.credits30, 'credit', 'credits') : '',
      ].filter(Boolean);
      add({
        check: 'inactive-but-used',
        clientName: name,
        problem: `Marked inactive, and used in the last ${RECENT_DAYS} days: ${parts.join(' and ')}.`,
        ...(use.lastUseDay ? { detail: `Last used on ${formatDay(use.lastUseDay)}.` } : {}),
      });
    }

    if (inactive) continue;

    if (status && !status.ok) {
      const reason = unreadableReason(status.lastError);
      if (reason) {
        const lastOk = toDay(status.lastOkAt);
        add({
          check: 'unreadable',
          clientName: name,
          problem: reason,
          detail: lastOk
            ? `The last good read was on ${formatDay(lastOk)}, ${plural(Math.max(0, daysBetween(lastOk, today)), 'day', 'days')} ago.`
            : 'No read has worked yet.',
          error: status.lastError,
        });
      }
      // A snapshot that is not current says nothing about the last 60 days.
      continue;
    }

    const oldEnough = use.stackSince !== null && daysBetween(use.stackSince, today) >= IDLE_DAYS;
    // Nolia runs its work as pipeline agents, so its chat figures say nothing
    // about its use. The same rule as the dashboard's Nolia view.
    const product = snapshot.client_config?.product;
    const nolia = product ? product === 'nolia' : name.toLowerCase().startsWith('nolia');
    const unused =
      use.convs60 === 0 && use.runs60 === 0 && use.credits60 === 0 && use.modelSpend60 < MODEL_USE_FLOOR_USD;
    if (!client.config.devInstance && !nolia && oldEnough && unused) {
      add({
        check: 'idle',
        clientName: name,
        problem: `No conversations, scheduled runs, credits or model spend in the last ${IDLE_DAYS} days.`,
        detail: use.lastUseDay ? `Last used on ${formatDay(use.lastUseDay)}.` : 'No use on record.',
      });
    }
  }

  const order = new Map(CHECKS.map((c, i) => [c.id, i]));
  return found.sort(
    (a, b) => (order.get(a.check) ?? 0) - (order.get(b.check) ?? 0) || a.clientName.localeCompare(b.clientName)
  );
}

/**
 * What Home shows without the fleet data: the record problems, and the
 * trials that ended. The usage checks need the fleet index, which Home does
 * not load.
 */
export function recordSummary(
  clients: readonly CheckClient[],
  row: (clientName: string) => ClientMetadata | undefined,
  isInactive: (clientName: string) => boolean,
  today: string
): { recordProblems: number; trialsEnded: number } {
  return {
    recordProblems: runChecks({ clients, row, isInactive, fleet: null, today }).length,
    trialsEnded: trialRows(clients, row, isInactive, today).filter((t) => t.timing.state === 'ended').length,
  };
}
